import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Ticket, Trophy, Clock, Wallet } from 'lucide-react';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import axios from 'axios';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyTickets = () => {
  const { connected, publicKey } = useWallet();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (connected && publicKey) {
      fetchMyTickets();
    } else {
      setLoading(false);
    }
  }, [connected, publicKey]);

  const fetchMyTickets = async () => {
    try {
      const response = await axios.get(`${API}/tickets/my/${publicKey.toString()}`);
      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (ticket) => {
    if (ticket.is_winner) {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-bold bg-sol-green/20 text-sol-green border border-sol-green/30">
          WINNER
        </span>
      );
    }
    if (ticket.draw_id) {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-bold bg-white/10 text-white/60 border border-white/10">
          Played
        </span>
      );
    }
    return (
      <span className="px-3 py-1 rounded-full text-xs font-bold bg-sol-purple/20 text-sol-purple border border-sol-purple/30">
        Active
      </span>
    );
  };

  if (!connected) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" data-testid="my-tickets-page">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md mx-auto px-4"
        >
          <div className="w-20 h-20 rounded-full bg-sol-purple/10 flex items-center justify-center mx-auto mb-6">
            <Wallet className="w-10 h-10 text-sol-purple" />
          </div>
          <h2 className="font-heading text-2xl font-bold mb-4">Connect Wallet</h2>
          <p className="text-white/60 mb-8">
            Connect your wallet to view your tickets
          </p>
          <WalletMultiButton className="!bg-sol-green !text-black !font-bold !px-8 !py-3 !rounded-lg" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="my-tickets-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-2">
            My Tickets
          </h1>
          <p className="text-white/60">
            All your purchased lottery tickets
          </p>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-sol-green border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Empty State */}
        {!loading && tickets.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-6">
              <Ticket className="w-10 h-10 text-white/40" />
            </div>
            <h3 className="font-heading text-xl font-bold mb-2">No Tickets Yet</h3>
            <p className="text-white/60 mb-8">
              Buy your first ticket and try your luck!
            </p>
            <a href="/play" className="btn-primary inline-block">
              <span>Play Now</span>
            </a>
          </motion.div>
        )}

        {/* Tickets Grid */}
        {!loading && tickets.length > 0 && (
          <div className="grid gap-6">
            {tickets.map((ticket, index) => (
              <motion.div
                key={ticket.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`card-neon rounded-xl p-6 ${ticket.is_winner ? 'border-sol-green/50 animate-pulse-neon' : ''}`}
                data-testid={`ticket-${ticket.id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-heading text-lg font-bold">
                        Ticket #{ticket.id.slice(0, 8)}
                      </h3>
                      {getStatusBadge(ticket)}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-white/40">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {format(new Date(ticket.created_at), 'MMM dd, yyyy HH:mm')}
                      </span>
                      <span className="font-mono">{ticket.total_sol} USDC</span>
                    </div>
                  </div>
                  
                  {ticket.matched_numbers > 0 && (
                    <div className="flex items-center gap-2 bg-sol-green/10 px-4 py-2 rounded-lg">
                      <Trophy className="w-5 h-5 text-sol-green" />
                      <span className="font-bold text-sol-green">
                        {ticket.matched_numbers} Matches
                      </span>
                    </div>
                  )}
                </div>

                {/* Fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {ticket.fields.map((field) => (
                    <div
                      key={field.field_number}
                      className="bg-white/5 rounded-lg p-4"
                    >
                      <p className="text-xs text-white/40 mb-2">Field {field.field_number}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {field.selected_numbers.map((num) => (
                          <div
                            key={num}
                            className="w-8 h-8 rounded-full bg-sol-green/20 text-sol-green flex items-center justify-center font-mono text-sm font-bold"
                          >
                            {num}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyTickets;
